import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Input, Textarea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Gift, Plus, X, Copy, Check, Users, Settings, Database } from 'lucide-react';
interface ParticipantInput {
  name: string;
  email: string;
}
interface Participant {
  name: string;
  email?: string;
  assignedTo: string;
  hasRevealed: boolean;
}
interface Game {
  id: string;
  gameName: string;
  createdAt: number;
  rules: {
    allowedGifts: string;
    budget: number;
    currency: string;
  };
  participants: Participant[];
}
const DEFAULT_RULES = `📜 MEMO FROM THE NORTH POLE

Rule #1: Loose lips sink ships. Keep your person a secret!

Rule #2: Don't break the bank (or the rules). Stick to the budget.

Rule #3: Wrap something nice. No cash allowed!`;
export function SecretSanta() {
  const [step, setStep] = useState<'participants' | 'rules' | 'share'>('participants');
  const [gameName, setGameName] = useState('');
  const [budget, setBudget] = useState('1000');
  const [currency, setCurrency] = useState('INR');
  const [allowedGifts, setAllowedGifts] = useState(DEFAULT_RULES);
  const [participants, setParticipants] = useState<ParticipantInput[]>([{
    name: '',
    email: ''
  }, {
    name: '',
    email: ''
  }, {
    name: '',
    email: ''
  }]);
  const [gameLink, setGameLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const addParticipant = () => {
    setParticipants([...participants, {
      name: '',
      email: ''
    }]);
  };
  const removeParticipant = (index: number) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };
  const updateParticipant = (index: number, field: 'name' | 'email', value: string) => {
    const updated = [...participants];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setParticipants(updated);
  };
  const generateUniqueGameId = (baseName: string): string => {
    const savedGames = localStorage.getItem('secretSantaGames');
    const games: Game[] = savedGames ? JSON.parse(savedGames) : [];
    let gameId: string;
    let attempts = 0;
    const maxAttempts = 100;
    do {
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 6);
      const baseSlug = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      gameId = `${baseSlug}-${timestamp}-${random}`;
      attempts++;
      if (attempts >= maxAttempts) {
        throw new Error('Failed to generate unique game ID');
      }
    } while (games.some(g => g.id === gameId));
    return gameId;
  };
  const generatePairings = () => {
    try {
      // Validate participants
      const validParticipants = participants.filter(p => p.name.trim() !== '');
      if (validParticipants.length < 3) {
        setError('You need at least 3 participants with names');
        return;
      }
      // Check for duplicate names
      const names = validParticipants.map(p => p.name.trim().toLowerCase());
      if (new Set(names).size !== names.length) {
        setError('All participant names must be unique');
        return;
      }
      if (!gameName.trim()) {
        setError('Please enter a game name');
        return;
      }
      setError('');
      // Shuffle algorithm with validation
      const givers = [...validParticipants];
      const receivers = [...validParticipants];
      let shuffleAttempts = 0;
      let valid = false;
      while (!valid && shuffleAttempts < 100) {
        // Fisher-Yates shuffle
        for (let i = receivers.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [receivers[i], receivers[j]] = [receivers[j], receivers[i]];
        }
        // Check if anyone gets themselves
        valid = true;
        for (let i = 0; i < givers.length; i++) {
          if (givers[i].name === receivers[i].name) {
            valid = false;
            break;
          }
        }
        shuffleAttempts++;
      }
      if (!valid) {
        setError('Failed to generate valid pairings. Please try again.');
        return;
      }
      // Generate unique game ID
      const gameId = generateUniqueGameId(gameName);
      const gameParticipants: Participant[] = givers.map((giver, index) => ({
        name: giver.name.trim(),
        email: giver.email.trim() || undefined,
        assignedTo: receivers[index].name.trim(),
        hasRevealed: false
      }));
      const newGame: Game = {
        id: gameId,
        gameName: gameName.trim(),
        createdAt: Date.now(),
        rules: {
          budget: parseInt(budget) || 1000,
          currency,
          allowedGifts: allowedGifts.trim()
        },
        participants: gameParticipants
      };
      // Save to localStorage with validation
      try {
        const savedGames = localStorage.getItem('secretSantaGames');
        const games: Game[] = savedGames ? JSON.parse(savedGames) : [];
        games.push(newGame);
        localStorage.setItem('secretSantaGames', JSON.stringify(games));
        // Verify save was successful
        const verifyGames = localStorage.getItem('secretSantaGames');
        const verifyParsed: Game[] = verifyGames ? JSON.parse(verifyGames) : [];
        const savedGame = verifyParsed.find(g => g.id === gameId);
        if (!savedGame) {
          throw new Error('Failed to verify game was saved');
        }
        console.log('✅ Game saved successfully:', gameId);
      } catch (saveError) {
        console.error('❌ Error saving game:', saveError);
        setError('Failed to save game. Please try again.');
        return;
      }
      // Generate link
      const link = `${window.location.origin}/fun/secret-santa/${gameId}`;
      setGameLink(link);
      setStep('share');
    } catch (err) {
      console.error('Error generating game:', err);
      setError('An error occurred. Please try again.');
    }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(gameLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const viewStoredGames = () => {
    const savedGames = localStorage.getItem('secretSantaGames');
    if (savedGames) {
      const games: Game[] = JSON.parse(savedGames);
      console.log('📦 Stored Games:', games);
      alert(`Found ${games.length} game(s) in storage. Check console for details.`);
    } else {
      alert('No games found in storage.');
    }
  };
  const clearAllGames = () => {
    if (confirm('Are you sure you want to delete ALL games? This cannot be undone.')) {
      localStorage.removeItem('secretSantaGames');
      alert('All games have been deleted.');
    }
  };
  return <div className="container mx-auto px-4 py-8 max-w-4xl" data-id="element-421">
      <div className="mb-8 text-center" data-id="element-422">
        <h1 className="text-4xl font-bold mb-2" data-id="element-423">Secret Santa Organizer</h1>
        <p className="text-muted-foreground" data-id="element-424">
          Create a game, set rules, and invite friends!
        </p>
      </div>

      {/* Debug Tools */}
      <div className="mb-4 flex justify-end" data-id="element-425">
        <Button variant="outline" size="sm" onClick={() => setShowDebug(!showDebug)} data-id="element-426">
          <Database className="w-4 h-4 mr-2" data-id="element-427" />
          Debug Tools
        </Button>
      </div>

      {showDebug && <Card className="mb-6 border-orange-500/50" data-id="element-428">
          <CardHeader data-id="element-429">
            <CardTitle className="text-sm" data-id="element-430">Debug & Validation Tools</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-id="element-431">
            <div className="flex gap-2" data-id="element-432">
              <Button onClick={viewStoredGames} variant="outline" size="sm" data-id="element-433">
                View Stored Games
              </Button>
              <Button onClick={clearAllGames} variant="outline" size="sm" className="text-destructive" data-id="element-434">
                Clear All Games
              </Button>
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-435">
              Use these tools to check localStorage and debug issues. Check
              browser console for detailed logs.
            </p>
          </CardContent>
        </Card>}

      <div className="flex justify-center mb-8" data-id="element-436">
        <div className="flex items-center gap-2 text-sm" data-id="element-437">
          <div className={`px-3 py-1 rounded-full ${step === 'participants' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} data-id="element-438">
            1. Participants
          </div>
          <div className="w-8 h-px bg-border" data-id="element-439"></div>
          <div className={`px-3 py-1 rounded-full ${step === 'rules' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} data-id="element-440">
            2. Rules
          </div>
          <div className="w-8 h-px bg-border" data-id="element-441"></div>
          <div className={`px-3 py-1 rounded-full ${step === 'share' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} data-id="element-442">
            3. Share
          </div>
        </div>
      </div>

      {step === 'participants' && <Card data-id="element-443">
          <CardHeader data-id="element-444">
            <CardTitle className="flex items-center gap-2" data-id="element-445">
              <Users className="w-5 h-5" data-id="element-446" />
              Add Participants
            </CardTitle>
            <CardDescription data-id="element-447">
              Enter names for everyone playing (min 3). Email is optional.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-448">
            <Input label="Game Name" value={gameName} onChange={e => setGameName(e.target.value)} placeholder="e.g., Office Secret Santa 2025" data-id="element-449" />

            <div className="space-y-3" data-id="element-450">
              <label className="block text-sm font-medium" data-id="element-451">Participants</label>
              {participants.map((participant, index) => <div key={index} className="flex gap-2 items-start" data-id="element-452">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-453">
                    <Input value={participant.name} onChange={e => updateParticipant(index, 'name', e.target.value)} placeholder={`Name ${index + 1} *`} data-id="element-454" />
                    <Input type="email" value={participant.email} onChange={e => updateParticipant(index, 'email', e.target.value)} placeholder={`Email ${index + 1} (optional)`} data-id="element-455" />
                  </div>
                  {participants.length > 3 && <Button variant="outline" onClick={() => removeParticipant(index)} className="shrink-0" data-id="element-456">
                      <X className="w-4 h-4" data-id="element-457" />
                    </Button>}
                </div>)}
            </div>

            <Button onClick={addParticipant} variant="outline" className="w-full border-dashed" data-id="element-458">
              <Plus className="w-4 h-4 mr-2" data-id="element-459" />
              Add Another Participant
            </Button>

            {error && <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-md flex items-center gap-2" data-id="element-460">
                <X className="w-4 h-4" data-id="element-461" /> {error}
              </p>}

            <Button onClick={() => setStep('rules')} className="w-full" data-id="element-462">
              Next: Set Rules
            </Button>
          </CardContent>
        </Card>}

      {step === 'rules' && <Card data-id="element-463">
          <CardHeader data-id="element-464">
            <CardTitle className="flex items-center gap-2" data-id="element-465">
              <Settings className="w-5 h-5" data-id="element-466" />
              Game Rules
            </CardTitle>
            <CardDescription data-id="element-467">
              Set the budget and gift guidelines
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-468">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-469">
              <Select label="Currency" value={currency} onChange={e => setCurrency(e.target.value)} options={[{
            value: 'INR',
            label: 'INR (₹) - Indian Rupee'
          }, {
            value: 'USD',
            label: 'USD ($) - US Dollar'
          }, {
            value: 'EUR',
            label: 'EUR (€) - Euro'
          }, {
            value: 'GBP',
            label: 'GBP (£) - British Pound'
          }, {
            value: 'AUD',
            label: 'AUD (A$) - Australian Dollar'
          }]} data-id="element-470" />
              <Input type="number" label="Budget Limit" value={budget} onChange={e => setBudget(e.target.value)} min="10" max="1000000" data-id="element-471" />
            </div>

            <Textarea label="Game Rules & Guidelines" value={allowedGifts} onChange={e => setAllowedGifts(e.target.value)} placeholder="Enter your custom rules..." rows={8} data-id="element-472" />

            <div className="flex gap-4 pt-4" data-id="element-473">
              <Button variant="outline" onClick={() => setStep('participants')} className="flex-1" data-id="element-474">
                Back
              </Button>
              <Button onClick={generatePairings} className="flex-1" data-id="element-475">
                Create Game & Get Link
              </Button>
            </div>
          </CardContent>
        </Card>}

      {step === 'share' && <Card className="border-green-500/50 shadow-lg" data-id="element-476">
          <CardHeader className="text-center" data-id="element-477">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-478">
              <Gift className="w-8 h-8" data-id="element-479" />
            </div>
            <CardTitle className="text-2xl text-green-700" data-id="element-480">
              Game Created Successfully!
            </CardTitle>
            <CardDescription data-id="element-481">
              Share this link with all participants. They'll select their name
              to see their match.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-id="element-482">
            <div className="flex gap-2" data-id="element-483">
              <Input value={gameLink} readOnly className="font-mono text-sm bg-muted" data-id="element-484" />
              <Button onClick={copyLink} className="shrink-0" data-id="element-485">
                {copied ? <Check className="w-4 h-4" data-id="element-486" /> : <Copy className="w-4 h-4" data-id="element-487" />}
              </Button>
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2 text-sm" data-id="element-488">
              <p className="font-semibold" data-id="element-489">Game Summary:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1" data-id="element-490">
                <li data-id="element-491">Game: {gameName}</li>
                <li data-id="element-492">
                  Participants:{' '}
                  {participants.filter(p => p.name.trim()).length}
                </li>
                <li data-id="element-493">
                  Budget:{' '}
                  {currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency}{' '}
                  {budget}
                </li>
              </ul>
            </div>

            <Button onClick={() => window.location.reload()} variant="outline" className="w-full" data-id="element-494">
              Create Another Game
            </Button>
          </CardContent>
        </Card>}
    </div>;
}